"use client"

import { useState, useEffect } from "react"
import { getAuth, onAuthStateChanged } from "firebase/auth"
import { getDatabase, ref as dbRef, set, get, push } from "firebase/database"
import "./raisedIssue.css"
import InfoIcon from "./infoIcon"
// Addition: Import date-fns for timestamp formatting
import { format, parse, isValid, formatDistanceToNow } from "date-fns"

const RaisedIssues = ({ adminView = false, selectedBrand = "" }) => {
  const [issues, setIssues] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [userId, setUserId] = useState(null)
  const [successMessage, setSuccessMessage] = useState("")
  const [brand, setBrand] = useState(adminView ? selectedBrand : localStorage.getItem("supplierBrand"))
  const [sortOrder, setSortOrder] = useState("desc") // Addition: State for sorting by date
  const [dateFilter, setDateFilter] = useState({ start: "", end: "" }) // Addition: State for date range filter
  const gifUrl = "/Images/New notification.gif"

  // Watch for brand change in admin mode
  useEffect(() => {
    if (adminView) {
      setBrand(selectedBrand)
    }
  }, [adminView, selectedBrand])

  // Send notification
  const sendNotification = async (userId, title, message, siteaddress) => {
    const notificationRef = dbRef(getDatabase(), `user_notifications/Raised Issues/${userId}`)
    const newNotificationRef = push(notificationRef)
    const newNotification = {
      title,
      message,
      gifUrl,
      siteaddress,
      timestamp: new Date().toISOString(),
      seen: false,
    }

    try {
      await set(newNotificationRef, newNotification)
      console.log("Notification sent successfully")
    } catch (error) {
      console.error("Error sending notification:", error)
    }
  }

  // Fetch issues on mount or brand/sort/filter change
  useEffect(() => {
    const auth = getAuth()

    if (adminView) {
      fetchIssues(brand)
    } else {
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        setLoading(true)
        setError("")

        if (user) {
          setUserId(user.uid)
          if (!brand) {
            setError("Brand not selected. Please log in again.")
            setLoading(false)
          } else {
            await fetchIssues(brand)
          }
        } else {
          setError("User not authenticated. Please log in again.")
          setLoading(false)
        }
      })

      return () => unsubscribe()
    }
  }, [brand, adminView])

  // Fetch issues from Firebase
  const fetchIssues = async (selectedBrand) => {
    try {
      const issuesRef = dbRef(getDatabase(), "issues")
      const snapshot = await get(issuesRef)

      if (snapshot.exists()) {
        const data = snapshot.val()
        let filteredIssues = []

        Object.keys(data).forEach((uid) => {
          Object.keys(data[uid]).forEach((issueId) => {
            const issue = data[uid][issueId] || {}
            const issueBrand = (issue.brand || "").trim().toLowerCase()
            const filterBrand = ((selectedBrand || brand) || "").trim().toLowerCase()
            const brandMatch = adminView && !selectedBrand ? true : issueBrand === filterBrand

            // Addition: Validate and parse timestamp
            const issueDate = issue.date
              ? parse(issue.date, "d/M/yyyy, h:mm:ss a", new Date())
              : null
            const isValidDate = issueDate && isValid(issueDate)

            // Addition: Apply date range filter
            const withinDateRange =
              !dateFilter.start || !dateFilter.end || !isValidDate
                ? true
                : issueDate >= new Date(dateFilter.start) && issueDate <= new Date(dateFilter.end)

            if (brandMatch && withinDateRange) {
              filteredIssues.push({
                issueId,
                userId: uid,
                ...issue,
                // Addition: Store parsed date for sorting
                parsedDate: isValidDate ? issueDate : null,
              })
            }
          })
        })

        // Addition: Sort issues by date
        filteredIssues.sort((a, b) => {
          const statusA = a.status || ""
          const statusB = b.status || ""
          
          // Prioritize pending status
          if (statusA === "Pending" && statusB !== "Pending") return -1
          if (statusA !== "Pending" && statusB === "Pending") return 1

          // Sort by date (newest or oldest based on sortOrder)
          if (a.parsedDate && b.parsedDate) {
            return sortOrder === "desc"
              ? b.parsedDate - a.parsedDate
              : a.parsedDate - b.parsedDate
          }
          return 0
        })

        setIssues(filteredIssues)
        setError(filteredIssues.length === 0 ? "No issues found for this brand or date range." : "")
      } else {
        setError("No issues found in the database.")
      }
    } catch (err) {
      setError("Error fetching issues: " + err.message)
    } finally {
      setLoading(false)
    }
  }

  // Handle Mark As Solved
  const handleMarkAsSolved = async (issueId) => {
    const issue = issues.find((issue) => issue.issueId === issueId)

    if (issue && issue.status === "Pending") {
      const userIdToUpdate = issue.userId
      const database = getDatabase()
      const issueRef = dbRef(database, `issues/${userIdToUpdate}/${issueId}`)

      try {
        await set(issueRef, {
          ...issue,
          status: "Solved",
        })

        await sendNotification(
          userIdToUpdate,
          "Issue Solved",
          `Your issue has been resolved for site address: ${issue.siteAddress}`,
          issue.siteAddress
        )

        setIssues((prevIssues) =>
          prevIssues.map((i) => (i.issueId === issueId ? { ...i, status: "Solved" } : i))
        )

        setSuccessMessage("Issue marked as solved successfully!")
        setTimeout(() => setSuccessMessage(""), 3000)
      } catch (error) {
        console.error("Error updating issue or sending notification:", error)
        setError("Failed to update issue. Please try again.")
      }
    }
  }

  // Addition: Handle sort toggle
  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"))
    fetchIssues(brand) // Re-fetch to apply new sort order
  }

  // Addition: Handle date filter change
  const handleDateFilterChange = (e) => {
    const { name, value } = e.target
    setDateFilter((prev) => ({ ...prev, [name]: value }))
  }

  // Addition: Apply date filter
  const applyDateFilter = () => {
    fetchIssues(brand)
  }

  // Addition: Export issues to CSV
  const exportToCSV = () => {
    const headers = ["Date", "Type", "Company", "Brand", "Grade", "Site Address", "Description", "Status"]
    const rows = issues.map((issue) => [
      issue.date || "N/A",
      issue.issueType || "N/A",
      issue.companyName || "N/A",
      issue.brand || "N/A",
      issue.grade || "N/A",
      issue.siteAddress || "N/A",
      issue.description || "N/A",
      issue.status || "N/A",
    ])

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n")

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.download = `issues_${new Date().toISOString()}.csv`
    link.click()
  }

  return (
    <div className="raised-issues-container">
      <div className="raised-issues-header">
        <h1>
          Raised Issues{" "}
          {adminView && selectedBrand ? `- ${selectedBrand}` : adminView ? "- All Brands" : ""}
          <InfoIcon text="This section allows you to track and address issues raised by buyers regarding your products, deliveries, or services. Buyers can report concerns such as delayed shipments, product quality issues, or order discrepancies. As a supplier, you can view these issues, communicate with buyers for resolution, and provide necessary updates. Resolving issues promptly helps in maintaining trust and ensuring smooth business operations." />
        </h1>
      </div>

      {/* Addition: Date filter and sort controls */}
      {/* <div className="issues-controls">
        <div className="date-filter">
          <label>
            Start Date:
            <input
              type="date"
              name="start"
              value={dateFilter.start}
              onChange={handleDateFilterChange}
            />
          </label>
          <label>
            End Date:
            <input
              type="date"
              name="end"
              value={dateFilter.end}
              onChange={handleDateFilterChange}
            />
          </label>
          <button onClick={applyDateFilter}>Apply Filter</button>
        </div>
        <button onClick={toggleSortOrder}>
          Sort by Date ({sortOrder === "desc" ? "Newest First" : "Oldest First"})
        </button>
        <button onClick={exportToCSV}>Export to CSV</button>
      </div> */}

      {successMessage && <div className="success-message">{successMessage}</div>}

      {loading ? (
        <div className="loading-state">Loading issues...</div>
      ) : error ? (
        <div className="error-state">{error}</div>
      ) : (
        <div className="issues-table-container">
          <table className="issues-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Company</th>
                <th>Brand</th>
                <th>Grade</th>
                <th>Site Address</th>
                <th>Description</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {issues.map((issue) => {
                const status = issue.status || "Unknown"
                // Addition: Format timestamp or show fallback
                const displayDate =
                  issue.parsedDate && isValid(issue.parsedDate)
                    ? `${format(issue.parsedDate, "dd/MM/yyyy, hh:mm:ss a")} (${formatDistanceToNow(issue.parsedDate, { addSuffix: true })})`
                    : "Invalid Date"
                return (
                  <tr
                    key={`${issue.userId}-${issue.issueId}`}
                    className={status === "Pending" ? "pending-row" : "solved-row"}
                  >
                    <td title="Timestamp in IST">{displayDate}</td>
                    <td>{issue.issueType || "N/A"}</td>
                    <td>{issue.companyName || "N/A"}</td>
                    <td>{issue.brand || "N/A"}</td>
                    <td>{issue.grade || "N/A"}</td>
                    <td>{issue.siteAddress || "N/A"}</td>
                    <td>{issue.description || "N/A"}</td>
                    <td>
                      <span className={`status-badge status-${status.toLowerCase()}`}>{status}</span>
                    </td>
                    <td>
                      {status === "Pending" ? (
                        <button className="solve-button" onClick={() => handleMarkAsSolved(issue.issueId)}>
                          Mark as Solved
                        </button>
                      ) : (
                        <span className="solved-text">Resolved</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default RaisedIssues;